import { useState, useCallback } from "react";
import {
  SelectField,
  useSelectQuery,
  Typography,
  Button,
  Switch,
} from "@smartrent/ui";
import { PaginatedResponse } from "@smartrent/hooks";
import { StyleSheet, View } from "react-native";

import { useDrawerNav } from "@/common/AppDrawer";
import { useCMWFeatureFlagMutation } from "@/api/cmw-feature-flags";
import { instance } from "@/lib/hooks";

// Simple types matching what the lookup APIs actually return
type OrganizationLookup = {
  id: number;
  name: string;
};

type GroupLookup = {
  id: number;
  marketing_name: string;
  organization_name: string;
};

export function AddFeatureFlagGate({
  flagName,
  refetch,
  entity: initialEntity,
}: {
  flagName: string;
  refetch?: () => void;
  entity?: string;
}) {
  const [organization, setOrganization] = useState<
    OrganizationLookup | undefined
  >(undefined);
  const [group, setGroup] = useState<GroupLookup | undefined>(undefined);
  const [entity, setEntity] = useState(initialEntity || null);
  const [enabled, setEnabled] = useState(false);
  const drawer = useDrawerNav();

  const orgQueryProps = useSelectQuery(
    (inputValue) => ["organization-lookup", { inputValue }] as const,
    function fetch({ queryKey, pageParam = 1 }) {
      const [, { inputValue }] = queryKey;
      return instance
        .get("/organizations", {
          params: {
            name: inputValue ? inputValue : undefined,
            page: pageParam,
            limit: 500,
          },
        })
        .then((response) => response.data as PaginatedResponse);
    },
    {},
    {
      inputDebounceInterval: 250,
    }
  );

  const groupQueryProps = useSelectQuery(
    (inputValue) => ["group-lookup", { inputValue }] as const,
    function fetch({ queryKey, pageParam = 1 }) {
      const [, { inputValue }] = queryKey;
      return instance
        .get("/groups", {
          params: {
            name: inputValue ? inputValue : undefined,
            page: pageParam,
            include_organization: true,
            limit: 500,
          },
        })
        .then((response) => response.data as PaginatedResponse);
    },
    {},
    {
      inputDebounceInterval: 250,
    }
  );

  const [updateFeatureFlag] = useCMWFeatureFlagMutation();
  const onSubmit = useCallback(async () => {
    if (!(organization || group)) {
      return;
    }

    const payload: { enabled: boolean } & Record<string, any> = { enabled };

    if (organization) {
      payload.organization_id = organization.id;
    } else if (group) {
      payload.group_id = group.id;
    }

    await updateFeatureFlag({
      flag: flagName,
      payload,
    });

    if (refetch) {
      refetch();
    }

    drawer.pop();
  }, [
    flagName,
    updateFeatureFlag,
    organization,
    group,
    enabled,
    drawer,
    refetch,
  ]);

  return (
    <View style={styles.container}>
      {entity ? null : (
        <View>
          <Typography type="title4">
            Do you want to add an organization or a group?
          </Typography>
          <View style={styles.marginContainer}>
            <Button onPress={() => setEntity("organization")}>
              Organization
            </Button>
            <Typography type="body" style={styles.center}>
              - or -
            </Typography>
            <Button onPress={() => setEntity("group")}>Group</Button>
          </View>
        </View>
      )}
      {entity === "organization" ? (
        <SelectField
          {...(orgQueryProps as any)}
          name="organization"
          label="Select Organization"
          getOptionValue={(option: OrganizationLookup) => option.id}
          getOptionLabel={(option: OrganizationLookup) => option.name}
          required
          value={organization}
          onChange={(value) =>
            setOrganization(value as OrganizationLookup | undefined)
          }
        />
      ) : null}

      {entity === "group" ? (
        <SelectField
          {...(groupQueryProps as any)}
          name="group"
          label="Select Group"
          getOptionValue={(option: GroupLookup) => option.id}
          getOptionLabel={(option: GroupLookup) =>
            `${option.marketing_name} - ${option.organization_name}`
          }
          required
          value={group}
          onChange={(value) => setGroup(value as GroupLookup | undefined)}
        />
      ) : null}

      {entity ? (
        <View style={styles.marginContainer}>
          <Switch
            name="enabled"
            label="Enabled"
            assistiveText={`Whether the feature is enabled or not for this ${entity}`}
            onValueChange={setEnabled}
            value={enabled}
          />
        </View>
      ) : null}

      {group || organization ? (
        <View style={styles.marginContainer}>
          <Button onPress={onSubmit}>Save</Button>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  marginContainer: {
    marginTop: 16,
  },
  center: {
    textAlign: "center",
    marginVertical: 8,
  },
});
